import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Slider } from '@/components/ui/slider';
import type { SettingsResponse } from '@/core/types';
import { api } from '@/lib/api';
import { COMMON_TIMEZONES } from '@/lib/timezones';

export function TimezoneSection({ settings }: { settings: SettingsResponse }) {
  const [tz, setTz] = useState(settings.user.timezone);
  const [grace, setGrace] = useState(settings.user.grace_hours);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: api.saveTimezone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['streak', 'overall'] });
      toast.success('Time zone saved');
    },
    onError: (err: Error) => toast.error('Failed to save', { description: err.message }),
  });

  const dirty = tz !== settings.user.timezone || grace !== settings.user.grace_hours;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ timezone: tz, grace_hours: grace });
      }}
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1.5">
        <Label>Timezone</Label>
        <NativeSelect value={tz} onChange={(e) => setTz(e.target.value)}>
          {COMMON_TIMEZONES.map((zone) => (
            <option key={zone} value={zone}>
              {zone}
            </option>
          ))}
        </NativeSelect>
        <p className="text-muted-foreground text-xs">
          Used to compute when a day starts and ends for your streak.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <Label>Grace hours</Label>
          <span className="font-mono text-sm tabular-nums">{grace}h</span>
        </div>
        <Slider
          value={[grace]}
          onValueChange={(v) => setGrace(v[0] ?? 0)}
          min={0}
          max={6}
          step={1}
        />
        <p className="text-muted-foreground text-xs">
          A session finished before {String(grace).padStart(2, '0')}:00 local time still counts
          toward yesterday's streak. Default 3.
        </p>
      </div>

      <div>
        <Button type="submit" disabled={!dirty || mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
